import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  CircularProgress,
  Paper,
  Box,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import { useParams, Link, useNavigate } from "react-router-dom";

function FolderProducts() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_name: "",
    sku: "",
    price: "",
    quantity: "",
    web_link: "",
    tags: "",
    notes: "",
    image: null,
    imagePreview: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [folders, setFolders] = useState([]);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchFolders();
    fetchProducts();
  }, [folderId]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${folderId}/products`
      );
      setProducts(res.data);
    } catch (err) {
      console.error("Eroare la fetch produse:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    const res = await axios.get(
      "https://server-9p4hx.kinsta.app/api/stocmanager/folders"
    );
    setFolders(res.data);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, image: file, imagePreview: previewUrl }));
  };

  const handleAddProduct = async () => {
    try {
      const form = new FormData();
      form.append("product_name", formData.product_name);
      form.append("sku", formData.sku);
      form.append("price", formData.price);
      form.append("quantity", formData.quantity);
      form.append("web_link", formData.web_link);
      form.append("tags", formData.tags);
      form.append("notes", formData.notes);
      form.append("folder_id", folderId);
      if (formData.image instanceof File) {
        form.append("image", formData.image);
      }

      await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/products/upload-image-s3",
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setDialogOpen(false);
      setSnackbarOpen(true);
      setFormData({
        product_name: "",
        sku: "",
        price: "",
        quantity: "",
        web_link: "",
        tags: "",
        notes: "",
        image: null,
        imagePreview: "",
      });
      fetchProducts();
    } catch (err) {
      console.error("Eroare la adăugare produs în S3:", err);
      alert("Eroare la salvare produs.");
    }
  };

  const handleMenuClick = (event, product) => {
    setSelectedProduct(product);
    setAnchorEl(event.currentTarget);
  };

  const handleMove = async () => {
    await axios.put(
      `https://server-9p4hx.kinsta.app/api/stocmanager/products/${selectedProduct.id}/move`,
      {
        folderId: targetFolderId,
      }
    );
    setAnchorEl(null);
    setMoveDialogOpen(false);
    fetchProducts();
  };

  const handleDelete = async () => {
    await axios.delete(
      `https://server-9p4hx.kinsta.app/api/stocmanager/products/${selectedProduct.id}`
    );
    setAnchorEl(null);
    fetchProducts();
  };

  const totalSum = products.reduce(
    (acc, prod) => acc + parseFloat(prod.price || 0) * (prod.quantity || 1),
    0
  );
  const productsWithoutSKU = products.filter(
    (p) => !p.sku || p.sku.trim() === ""
  ).length;
  const totalQuantity = products.reduce(
    (acc, p) => acc + Number(p.quantity || 0),
    0
  );
  const folderName =
    folders.find((f) => f.id === parseInt(folderId))?.name || "";

  return (
    <Container maxWidth={false} sx={{ pt: 2, px: { xs: 1.5, md: 3 } }}>
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={() => navigate(-1)}
            size="small"
            aria-label="Înapoi"
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            {folderName}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Adaugă produs
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Items
            </Typography>
            <Typography variant="h6">{products.length}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Produse
            </Typography>
            <Typography variant="h6">{totalQuantity}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Ciorne
            </Typography>
            <Typography variant="h6">{productsWithoutSKU}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Valoare
            </Typography>
            <Typography variant="h6">
              {Math.round(totalSum).toLocaleString()} RON
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Search bar */}
      <TextField
        placeholder="Caută după denumire sau SKU"
        variant="outlined"
        margin="dense"
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          sx: { borderRadius: 3, backgroundColor: "#fafafa" },
        }}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper sx={{ p: 2 }}>
          {products.length === 0 ? (
            <Typography>Nu există produse în acest folder.</Typography>
          ) : (
            products
              .filter(
                (p) =>
                  (p.product_name || "")
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  (p.sku || "").toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((prod) => (
                <Box
                  key={prod.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  borderBottom="1px solid #eee"
                  py={1.5}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/product/${prod.id}`)}
                  >
                    <Box
                      component="img"
                      src={prod.image_url || prod.image_base64}
                      alt={prod.product_name}
                      loading="lazy"
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        mr: 2,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {prod.product_name}
                      </Typography>
                      <Box display="flex" gap={3}>
                        <Typography variant="body2">SKU: {prod.sku}</Typography>
                        <Typography variant="body2">
                          Preț: {Math.round(prod.price).toLocaleString()} RON
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <IconButton onClick={(e) => handleMenuClick(e, prod)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              ))
          )}
        </Paper>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => navigate(`/edit-product/${selectedProduct?.id}`)}
        >
          Editare
        </MenuItem>
        <MenuItem
          onClick={() => {
            fetchFolders();
            setMoveDialogOpen(true);
            setAnchorEl(null);
          }}
        >
          Mută
        </MenuItem>
        <MenuItem onClick={handleDelete}>Șterge</MenuItem>
        <MenuItem
          onClick={() => {
            setFormData({ ...selectedProduct });
            setDialogOpen(true);
            setAnchorEl(null);
          }}
        >
          Clonează
        </MenuItem>
      </Menu>

      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)}>
        <DialogTitle>Mută produsul într-un alt folder</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <InputLabel id="select-folder">Folder</InputLabel>
            <Select
              labelId="select-folder"
              value={targetFolderId}
              onChange={(e) => setTargetFolderId(e.target.value)}
              fullWidth
            >
              {folders.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)}>Anulează</Button>
          <Button variant="contained" onClick={handleMove}>
            Mută
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>Adaugă Produs</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nume produs"
            margin="dense"
            value={formData.product_name}
            onChange={(e) =>
              setFormData({ ...formData, product_name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="SKU"
            margin="dense"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
          />
          <TextField
            fullWidth
            label="Preț (€)"
            margin="dense"
            type="number"
            value={formData.price}
            onChange={(e) =>
              setFormData({ ...formData, price: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Cantitate"
            margin="dense"
            type="number"
            value={formData.quantity}
            onChange={(e) =>
              setFormData({ ...formData, quantity: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Link Web"
            margin="dense"
            value={formData.web_link}
            onChange={(e) =>
              setFormData({ ...formData, web_link: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Tags"
            margin="dense"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          />
          <TextField
            fullWidth
            label="Notițe"
            margin="dense"
            multiline
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
          />
          <Button component="label" variant="outlined" sx={{ mt: 1 }}>
            Încarcă Imagine
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Anulează</Button>
          <Button variant="contained" onClick={handleAddProduct}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Produsul a fost adăugat!
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default FolderProducts;
